/**
 * Fairness & Model Cards Service — wraps /api/admin/ai/fairness/
 * Phase 4.8-B — WQ-198
 */

// === Types ===

export interface FairnessEvalRequest {
  model_id: string;
  dataset_id: string;
  protected_attributes: string[];
  metrics?: string[];
}

// === WQ-240 helpers (Phase 4.10 β5 — FairnessLauncher) ===

/**
 * Real backend shape for POST /api/admin/ai/fairness/evaluate.
 * Backend evaluates fairness on the experiment's best run; protected_attributes
 * is a Dict mapping attribute name to a list of values (one entry per row).
 */
export interface FairnessRunRequest {
  experiment_id: string;
  protected_attributes: Record<string, Array<string | number>>;
  problem_type: 'classification' | 'regression';
}

export interface FairnessRunResponse {
  experiment_id: string;
  overall_fairness_score: number;
  per_attribute_metrics: Record<string, unknown>;
  violations: Array<Record<string, unknown>>;
  recommendations: string[];
  problem_type: string;
}

export interface FairnessReport {
  id: string;
  model_id: string;
  dataset_id: string;
  overall_fairness_score: number | null;
  attribute_results: AttributeResult[];
  created_at: string;
}

export interface AttributeResult {
  attribute: string;
  metrics: Record<string, number>;
  disparate_impact: number | null;
  statistical_parity: number | null;
  group_sizes: Record<string, number>;
}

export interface ProtectedAttributeDetection {
  detected_attributes: string[];
  confidence: Record<string, number>;
  recommendations: string[];
}

export interface ModelCard {
  model_id: string;
  model_name: string;
  description: string;
  intended_use: string;
  limitations: string;
  training_data: string;
  evaluation_metrics: Record<string, unknown>;
  fairness_analysis: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

type FetchFn = (url: string, options?: RequestInit) => Promise<Response>;

// === API Functions ===

export async function evaluateFairness(
  authFetch: FetchFn,
  request: FairnessEvalRequest
): Promise<FairnessReport> {
  const res = await authFetch('/api/admin/ai/fairness/evaluate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  if (!res.ok) throw new Error(`Failed to evaluate fairness: ${res.status}`);
  return res.json();
}

export async function getFairnessReport(
  authFetch: FetchFn,
  modelId: string
): Promise<FairnessReport> {
  const res = await authFetch(`/api/admin/ai/fairness/report/${modelId}`);
  if (!res.ok) throw new Error(`Failed to get fairness report: ${res.status}`);
  return res.json();
}

export async function detectProtectedAttributes(
  authFetch: FetchFn,
  body: { dataset_id: string; column_names?: string[] }
): Promise<ProtectedAttributeDetection> {
  const res = await authFetch('/api/admin/ai/fairness/detect-attributes', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Failed to detect attributes: ${res.status}`);
  return res.json();
}

export async function generateModelCard(
  authFetch: FetchFn,
  body: { model_id: string; include_fairness?: boolean }
): Promise<ModelCard> {
  const res = await authFetch('/api/admin/ai/fairness/model-card/generate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Failed to generate model card: ${res.status}`);
  return res.json();
}

export async function getModelCard(
  authFetch: FetchFn,
  modelId: string
): Promise<ModelCard> {
  const res = await authFetch(`/api/admin/ai/fairness/model-card/${modelId}`);
  if (!res.ok) throw new Error(`Failed to get model card: ${res.status}`);
  return res.json();
}

export async function getModelCardMarkdown(
  authFetch: FetchFn,
  modelId: string
): Promise<string> {
  const res = await authFetch(`/api/admin/ai/fairness/model-card/${modelId}/markdown`);
  if (!res.ok) throw new Error(`Failed to get model card markdown: ${res.status}`);
  return res.text();
}

/**
 * Run a fairness evaluation against the real backend (WQ-240).
 * Wraps POST /api/admin/ai/fairness/evaluate with the actual request shape
 * (experiment-keyed, attribute → values map). Distinct from the older
 * `evaluateFairness` (model+dataset shape, kept for FairnessPanel compat).
 */
export async function runFairnessEvaluation(
  authFetch: FetchFn,
  request: FairnessRunRequest
): Promise<FairnessRunResponse> {
  const res = await authFetch('/api/admin/ai/fairness/evaluate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  if (!res.ok) throw new Error(`Failed to run fairness evaluation: ${res.status}`);
  return res.json();
}

/**
 * List experiments that have at least one completed run, suitable for the
 * fairness launcher's Step 1 picker. Reuses the AutoML experiments endpoint
 * (a fairness evaluation requires a best-run with stored metrics).
 */
export async function listFairnessExperiments(
  authFetch: FetchFn
): Promise<Array<{ id: string; name: string; status: string; problem_type: string; best_run_id: string | null }>> {
  const res = await authFetch('/api/admin/ai/automl/experiments?status=complete&limit=200');
  if (!res.ok) throw new Error(`Failed to list experiments: ${res.status}`);
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}
